import { cn } from '../../lib/utils';
import { Card } from './Card';

export interface LoadingCardProps {
  rows?: number;
  showHeader?: boolean;
  showFooter?: boolean;
  className?: string;
}

export function LoadingCard({
  rows = 3,
  showHeader = false,
  showFooter = false,
  className,
}: LoadingCardProps) {
  return (
    <Card className={cn('animate-pulse', className)}>
      {showHeader && (
        <div className="mb-4 pb-4 border-b border-surface-800">
          <div className="h-5 bg-surface-700 rounded w-1/3 mb-2" />
          <div className="h-3 bg-surface-700/50 rounded w-1/2" />
        </div>
      )}
      
      <div className="space-y-4">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="w-10 h-10 bg-surface-700 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-surface-700 rounded w-3/4" />
              <div className="h-3 bg-surface-700/50 rounded w-1/2" />
            </div>
            <div className="h-4 bg-surface-700 rounded w-16" />
          </div>
        ))}
      </div>
      
      {showFooter && (
        <div className="mt-4 pt-4 border-t border-surface-800">
          <div className="h-8 bg-surface-700 rounded w-24" />
        </div>
      )}
    </Card>
  );
}

// Skeleton variants for different use cases
export function LoadingStatCard({ className }: { className?: string }) {
  return (
    <Card className={cn('animate-pulse', className)}>
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-3 bg-surface-700/50 rounded w-20" />
          <div className="h-8 bg-surface-700 rounded w-32" />
        </div>
        <div className="w-12 h-12 bg-surface-700 rounded-xl" />
      </div>
    </Card>
  );
}

export function LoadingTableCard({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <Card className={cn('animate-pulse', className)}>
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-surface-800 mb-4">
        <div className="h-4 bg-surface-700 rounded w-1/4" />
        <div className="h-4 bg-surface-700 rounded w-1/4" />
        <div className="h-4 bg-surface-700 rounded w-1/4" />
        <div className="h-4 bg-surface-700 rounded w-1/4" />
      </div>
      
      {/* Rows */}
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-2">
            <div className="h-4 bg-surface-700/70 rounded w-1/4" />
            <div className="h-4 bg-surface-700/70 rounded w-1/4" />
            <div className="h-4 bg-surface-700/70 rounded w-1/4" />
            <div className="h-4 bg-surface-700/70 rounded w-1/4" />
          </div>
        ))}
      </div>
    </Card>
  );
}

export function LoadingProfileCard({ className }: { className?: string }) {
  return (
    <Card className={cn('animate-pulse text-center', className)}>
      {/* Avatar */}
      <div className="w-24 h-24 bg-surface-700 rounded-full mx-auto mb-4" />
      
      {/* Name */}
      <div className="h-6 bg-surface-700 rounded w-1/2 mx-auto mb-2" />
      
      {/* Badge */}
      <div className="h-5 bg-surface-700/50 rounded-full w-24 mx-auto mb-4" />
      
      {/* Info */}
      <div className="space-y-2 pt-4 border-t border-surface-800">
        <div className="h-4 bg-surface-700/50 rounded w-3/4 mx-auto" />
        <div className="h-3 bg-surface-700/30 rounded w-1/2 mx-auto" />
      </div>
    </Card>
  );
}

export function LoadingGridCard({ className }: { className?: string }) {
  return (
    <Card className={cn('animate-pulse', className)}>
      {/* Icon */}
      <div className="w-12 h-12 bg-surface-700 rounded-xl mx-auto mb-3" />
      
      {/* Title */}
      <div className="h-4 bg-surface-700 rounded w-2/3 mx-auto mb-2" />
      
      {/* Subtitle */}
      <div className="h-3 bg-surface-700/50 rounded w-1/2 mx-auto" />
    </Card>
  );
}

export default LoadingCard;
